// skipcq: JS-W1028, JS-0128
import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';

/**
 * MergeBeersDialog
 * Dialog for merging two beers with side-by-side comparison and attribute selection.
 */
function MergeBeersDialog({
  open,
  onClose,
  beer1,
  beer2,
  styleMap,
  breweryId,
  onMerge,
  loading,
  error,
}) {
  // Determine which beer IDs we're working with
  const beer1Id = beer1?.id || '';
  const beer2Id = beer2?.id || '';

  // State for which beer survives (defaults to beer1)
  const [survivingBeerId, setSurvivingBeerId] = useState(beer1Id);

  // State for which attribute to keep for each field
  const [selectedName, setSelectedName] = useState(beer1Id);
  const [selectedStyle, setSelectedStyle] = useState(beer1Id);
  const [selectedAbv, setSelectedAbv] = useState(beer1Id);
  const [selectedDescription, setSelectedDescription] = useState(beer1Id);

  // Reset state when dialog opens/closes or beers change
  React.useEffect(() => {
    if (open && beer1Id && beer2Id) {
      setSurvivingBeerId(beer1Id);
      setSelectedName(beer1Id);
      setSelectedStyle(beer1Id);
      setSelectedAbv(beer1Id);
      setSelectedDescription(beer1Id);
    }
  }, [open, beer1Id, beer2Id]);

  const handleSurvivingBeerChange = useCallback((event) => {
    setSurvivingBeerId(event.target.value);
  }, []);

  const handleNameChange = useCallback((event) => {
    setSelectedName(event.target.value);
  }, []);

  const handleStyleChange = useCallback((event) => {
    setSelectedStyle(event.target.value);
  }, []);

  const handleAbvChange = useCallback((event) => {
    setSelectedAbv(event.target.value);
  }, []);

  const handleDescriptionChange = useCallback((event) => {
    setSelectedDescription(event.target.value);
  }, []);

  // Compute merged beer data based on selections
  const mergedBeerData = useMemo(() => {
    if (!beer1 || !beer2) return null;

    const nameBeer = selectedName === beer1Id ? beer1 : beer2;
    const styleBeer = selectedStyle === beer1Id ? beer1 : beer2;
    const abvBeer = selectedAbv === beer1Id ? beer1 : beer2;
    const descBeer = selectedDescription === beer1Id ? beer1 : beer2;

    return {
      name: (nameBeer.name || '').trim(),
      style: (styleBeer.style || '').trim(),
      abv: abvBeer.abv || 0,
      description: descBeer.description?.trim() || null,
    };
  }, [
    beer1,
    beer2,
    beer1Id,
    selectedName,
    selectedStyle,
    selectedAbv,
    selectedDescription,
  ]);

  const handleMerge = useCallback(() => {
    if (!mergedBeerData || !survivingBeerId) return;

    const mergingBeerId =
      survivingBeerId === beer1Id ? beer2Id : beer1Id;

    onMerge({
      breweryId,
      survivingBeerId,
      mergingBeerId,
      mergedBeer: mergedBeerData,
    });
  }, [
    mergedBeerData,
    survivingBeerId,
    beer1Id,
    beer2Id,
    breweryId,
    onMerge,
  ]);

  const isValid = useMemo(() => {
    if (!mergedBeerData) return false;
    return (
      mergedBeerData.name.trim() !== '' &&
      mergedBeerData.style.trim() !== '' &&
      mergedBeerData.abv > 0 &&
      survivingBeerId &&
      (survivingBeerId === beer1Id || survivingBeerId === beer2Id)
    );
  }, [mergedBeerData, survivingBeerId, beer1Id, beer2Id]);

  if (!beer1 || !beer2) {
    return null;
  }

  const beer1Name = beer1.name || 'Unnamed Beer';
  const beer2Name = beer2.name || 'Unnamed Beer';
  const beer1Owner = beer1.breweryName || beer1.breweryId || 'Unknown taproom';
  const beer2Owner = beer2.breweryName || beer2.breweryId || 'Unknown taproom';
  const beer1Style = styleMap[beer1.style] || beer1.style || 'Unknown Style';
  const beer2Style = styleMap[beer2.style] || beer2.style || 'Unknown Style';
  const beer1Abv = beer1.abv ? `${beer1.abv}%` : 'N/A';
  const beer2Abv = beer2.abv ? `${beer2.abv}%` : 'N/A';
  const beer1Description = beer1.description || '—';
  const beer2Description = beer2.description || '—';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Merge Beers</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Surviving Beer Selection */}
        <Box sx={{
          mb: 3
        }}>
          <FormControl fullWidth>
            <FormLabel>Surviving Beer (Beer ID to Keep)</FormLabel>
            <RadioGroup
              name="surviving-beer"
              value={survivingBeerId}
              onChange={handleSurvivingBeerChange}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: 2,
                rowGap: 1,
                mt: 1,
              }}
            >
              <FormControlLabel
                value={beer1Id}
                control={<Radio />}
                label={`${beer1Name} — ${beer1Owner} (${beer1Id})`}
              />
              <FormControlLabel
                value={beer2Id}
                control={<Radio />}
                label={`${beer2Name} — ${beer2Owner} (${beer2Id})`}
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Side-by-side comparison */}
        <Grid container spacing={2}>
          {/* Name Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel>Name</FormLabel>
              <RadioGroup
                name="name-selection"
                value={selectedName}
                onChange={handleNameChange}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  columnGap: 2,
                  rowGap: 1,
                  mt: 1,
                }}
              >
                <FormControlLabel
                  value={beer1Id}
                  control={<Radio />}
                  label={beer1Name}
                />
                <FormControlLabel
                  value={beer2Id}
                  control={<Radio />}
                  label={beer2Name}
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Style Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel>Style</FormLabel>
              <RadioGroup
                name="style-selection"
                value={selectedStyle}
                onChange={handleStyleChange}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  columnGap: 2,
                  rowGap: 1,
                  mt: 1,
                }}
              >
                <FormControlLabel
                  value={beer1Id}
                  control={<Radio />}
                  label={beer1Style}
                />
                <FormControlLabel
                  value={beer2Id}
                  control={<Radio />}
                  label={beer2Style}
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* ABV Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel>ABV</FormLabel>
              <RadioGroup
                name="abv-selection"
                value={selectedAbv}
                onChange={handleAbvChange}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  columnGap: 2,
                  rowGap: 1,
                  mt: 1,
                }}
              >
                <FormControlLabel
                  value={beer1Id}
                  control={<Radio />}
                  label={beer1Abv}
                />
                <FormControlLabel
                  value={beer2Id}
                  control={<Radio />}
                  label={beer2Abv}
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Description Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel>Tasting Notes (Description)</FormLabel>
              <RadioGroup
                name="description-selection"
                value={selectedDescription}
                onChange={handleDescriptionChange}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  columnGap: 2,
                  rowGap: 1,
                  mt: 1,
                }}
              >
                <FormControlLabel
                  value={beer1Id}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" component="div">
                        {beer1Description}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value={beer2Id}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" component="div">
                        {beer2Description}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleMerge}
          variant="contained"
          color="primary"
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Merging...' : 'Merge Beers'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

MergeBeersDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  beer1: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    style: PropTypes.string,
    abv: PropTypes.number,
    description: PropTypes.string,
  }),
  beer2: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    style: PropTypes.string,
    abv: PropTypes.number,
    description: PropTypes.string,
  }),
  styleMap: PropTypes.objectOf(PropTypes.string).isRequired,
  breweryId: PropTypes.string.isRequired,
  onMerge: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

MergeBeersDialog.defaultProps = {
  beer1: null,
  beer2: null,
  loading: false,
  error: null,
};

export default MergeBeersDialog;
